import { useTransitionNavigate } from './PageTransition';
import { Hoverable } from './Hoverable';
import { productPath } from '../data/products';
import { color, font } from '../styles/tokens';

const cardHover = {
  transform: 'translateY(-6px)',
  boxShadow: '0 30px 60px -30px rgba(77,141,255,.55)',
  borderColor: 'rgba(77,141,255,.5)',
};

const ImageOrPlaceholder = ({ product, aspect = '4/3', radius = 0, borderBottom = true }) => {
  const frame = {
    position: 'relative',
    aspectRatio: aspect,
    overflow: 'hidden',
    background: '#0b0f17',
    borderRadius: radius,
    borderBottom: borderBottom ? '1px solid rgba(255,255,255,.08)' : 'none',
  };
  if (product.hasImg) {
    return (
      <div style={frame}>
        <img src={product.img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
    );
  }
  return (
    <div
      style={{
        ...frame,
        background: 'repeating-linear-gradient(135deg,#151b26 0 14px,#10141d 14px 28px)',
      }}
    >
      <span
        style={{
          position: 'absolute',
          left: 12,
          bottom: 12,
          font: `500 9px ${font.mono}`,
          textTransform: 'uppercase',
          color: color.textFaint,
          background: 'rgba(10,12,18,.85)',
          border: '1px solid rgba(255,255,255,.08)',
          padding: '4px 7px',
          borderRadius: 6,
        }}
      >
        product photo
      </span>
    </div>
  );
};

// Standard grid card. `reveal` toggles the scroll-in animation attribute.
export const ProductCard = ({ product, reveal = true }) => {
  const navigate = useTransitionNavigate();
  return (
    <Hoverable
      as="button"
      data-reveal={reveal ? '' : undefined}
      onClick={() => navigate(productPath(product.id))}
      style={{
        textAlign: 'left',
        background: color.panel,
        border: `1px solid ${color.panelBorder}`,
        borderRadius: 18,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s,border-color .35s',
      }}
      hoverStyle={cardHover}
    >
      <ImageOrPlaceholder product={product} />
      <div style={{ padding: 20 }}>
        <div style={{ font: `600 16px ${font.display}`, color: color.text }}>{product.name}</div>
        <div style={{ marginTop: 7, font: `400 13px/1.5 ${font.body}`, color: color.textDim }}>{product.tag}</div>
        <div style={{ marginTop: 14, font: `500 11px ${font.mono}`, color: color.accent }}>VIEW DETAILS →</div>
      </div>
    </Hoverable>
  );
};

// Compact related-product card (smaller text, no CTA line).
export const RelatedCard = ({ product }) => {
  const navigate = useTransitionNavigate();
  return (
    <Hoverable
      as="button"
      data-reveal=""
      onClick={() => navigate(productPath(product.id))}
      style={{
        textAlign: 'left',
        background: color.panel,
        border: `1px solid ${color.panelBorder}`,
        borderRadius: 18,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform .35s cubic-bezier(.16,1,.3,1),box-shadow .35s,border-color .35s',
      }}
      hoverStyle={cardHover}
    >
      <ImageOrPlaceholder product={product} />
      <div style={{ padding: 18 }}>
        <div style={{ font: `600 15px ${font.display}`, color: color.text }}>{product.name}</div>
        <div style={{ marginTop: 6, font: `400 12.5px/1.5 ${font.body}`, color: color.textDim }}>{product.tag}</div>
      </div>
    </Hoverable>
  );
};

// List-view row (Products page list layout).
export const ProductRow = ({ product }) => {
  const navigate = useTransitionNavigate();
  return (
    <Hoverable
      as="button"
      data-reveal=""
      onClick={() => navigate(productPath(product.id))}
      style={{
        textAlign: 'left',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        background: 'rgba(255,255,255,.02)',
        border: 'none',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        padding: '18px 22px',
        cursor: 'pointer',
        transition: 'background .25s',
        width: '100%',
      }}
      hoverStyle={{ background: 'rgba(77,141,255,.06)' }}
    >
      {product.hasImg ? (
        <div style={{ width: 56, height: 56, flex: 'none', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)', background: '#0b0f17' }}>
          <img src={product.img} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      ) : (
        <div style={{ width: 56, height: 56, flex: 'none', borderRadius: 12, background: 'repeating-linear-gradient(135deg,#151b26 0 10px,#10141d 10px 20px)', border: '1px solid rgba(255,255,255,.08)' }} />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: `600 15px ${font.display}`, color: color.text }}>{product.name}</div>
        <div style={{ marginTop: 4, font: `400 13px ${font.body}`, color: color.textDim }}>{product.tag}</div>
      </div>
      <span style={{ font: `500 12px ${font.mono}`, color: color.accent, flex: 'none' }}>DETAILS →</span>
    </Hoverable>
  );
};
